import _asyncToGenerator from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
var _excluded = ["placement", "fixed", "gutter", "flip", "shift", "slide", "overlap", "sameWidth", "fitViewport", "arrowPadding", "overflowPadding", "renderCallback"];
import _regeneratorRuntime from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/regenerator/index.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useRef, useState, useMemo } from 'react';
import { autoUpdate, computePosition, arrow, flip, offset, shift, size } from '@floating-ui/dom';
import { useEvent, useForceUpdate, useSafeLayoutEffect } from 'ariakit-utils/hooks';
import { flushSync } from 'react-dom';
import { useDialogState } from '../dialog/dialog-state.js';
var middlewares = {
  arrow: arrow,
  flip: flip,
  offset: offset,
  shift: shift,
  size: size
};

function createDOMRect(x, y, width, height) {
  if (x === void 0) {
    x = 0;
  }

  if (y === void 0) {
    y = 0;
  }

  if (width === void 0) {
    width = 0;
  }

  if (height === void 0) {
    height = 0;
  }

  if (typeof DOMRect === "function") {
    return new DOMRect(x, y, width, height);
  } // JSDOM doesn't support DOMRect constructor.


  var rect = {
    x: x,
    y: y,
    width: width,
    height: height,
    top: y,
    right: x + width,
    bottom: y + height,
    left: x
  };
  return _objectSpread(_objectSpread({}, rect), {}, {
    toJSON: function toJSON() {
      return rect;
    }
  });
}

function getDOMRect(anchorRect) {
  if (!anchorRect) return createDOMRect();
  var x = anchorRect.x,
      y = anchorRect.y,
      width = anchorRect.width,
      height = anchorRect.height;
  return createDOMRect(x, y, width, height);
}

function getAnchorElement(anchorRef, getAnchorRect) {
  // https://floating-ui.com/docs/virtual-elements
  var contextElement = anchorRef.current || undefined;
  return {
    contextElement: contextElement,
    getBoundingClientRect: function getBoundingClientRect() {
      var anchor = anchorRef.current;
      var anchorRect = getAnchorRect(anchor);

      if (anchorRect || !anchor) {
        return getDOMRect(anchorRect);
      }

      return anchor.getBoundingClientRect();
    }
  };
}

function isValidPlacement(flip) {
  return /^(?:top|bottom|left|right)(?:-(?:start|end))?$/.test(flip);
}
/**
 * Provides state for the `Popover` components.
 * @example
 * ```jsx
 * const popover = usePopoverState();
 * <PopoverDisclosure state={popover}>Disclosure</PopoverDisclosure>
 * <Popover state={popover}>Popover</Popover>
 * ```
 */


function usePopoverState(_temp) {
  var _ref3 = _temp === void 0 ? {} : _temp,
      _ref3$placement = _ref3.placement,
      placement = _ref3$placement === void 0 ? "bottom" : _ref3$placement,
      _ref3$fixed = _ref3.fixed,
      fixed = _ref3$fixed === void 0 ? false : _ref3$fixed,
      gutter = _ref3.gutter,
      _ref3$flip = _ref3.flip,
      flip = _ref3$flip === void 0 ? true : _ref3$flip,
      _ref3$shift = _ref3.shift,
      shift = _ref3$shift === void 0 ? 0 : _ref3$shift,
      _ref3$slide = _ref3.slide,
      slide = _ref3$slide === void 0 ? true : _ref3$slide,
      _ref3$overlap = _ref3.overlap,
      overlap = _ref3$overlap === void 0 ? false : _ref3$overlap,
      _ref3$sameWidth = _ref3.sameWidth,
      sameWidth = _ref3$sameWidth === void 0 ? false : _ref3$sameWidth,
      _ref3$fitViewport = _ref3.fitViewport,
      fitViewport = _ref3$fitViewport === void 0 ? false : _ref3$fitViewport,
      _ref3$arrowPadding = _ref3.arrowPadding,
      arrowPadding = _ref3$arrowPadding === void 0 ? 4 : _ref3$arrowPadding,
      _ref3$overflowPadding = _ref3.overflowPadding,
      overflowPadding = _ref3$overflowPadding === void 0 ? 8 : _ref3$overflowPadding,
      renderCallback = _ref3.renderCallback,
      props = _objectWithoutProperties(_ref3, _excluded);

  var dialog = useDialogState(props);

  var defaultGetAnchorRect = function defaultGetAnchorRect(anchor) {
    return (anchor == null ? void 0 : anchor.getBoundingClientRect()) || null;
  };

  var getAnchorRect = useEvent(props.getAnchorRect || defaultGetAnchorRect);
  var anchorRef = useRef(null);
  var popoverRef = useRef(null);
  var arrowRef = useRef(null);

  var _useState = useState(placement),
      _useState2 = _slicedToArray(_useState, 2),
      currentPlacement = _useState2[0],
      setCurrentPlacement = _useState2[1];

  var _useForceUpdate = useForceUpdate(),
      _useForceUpdate2 = _slicedToArray(_useForceUpdate, 2),
      rendered = _useForceUpdate2[0],
      render = _useForceUpdate2[1];

  useSafeLayoutEffect(function () {
    var _dialog$contentElemen;

    if (!((_dialog$contentElemen = dialog.contentElement) != null && _dialog$contentElemen.isConnected)) return;
    var popover = popoverRef.current;
    if (!popover) return;
    var anchor = getAnchorElement(anchorRef, getAnchorRect);
    var arrow = arrowRef.current;
    var arrowOffset = ((arrow == null ? void 0 : arrow.clientHeight) || 0) / 2;
    var finalGutter = typeof gutter === "number" ? gutter + arrowOffset : gutter != null ? gutter : arrowOffset;
    popover.style.setProperty("--popover-overflow-padding", overflowPadding + "px");

    var defaultRenderCallback = function defaultRenderCallback() {
      var update = /*#__PURE__*/function () {
        var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
          var middleware, fallbackPlacements, pos, x, y, _pos$middlewareData$a, arrowX, arrowY, dir;

          return _regeneratorRuntime.wrap(function _callee$(_context) {
            while (1) {
              switch (_context.prev = _context.next) {
                case 0:
                  if (dialog.mounted) {
                    _context.next = 2;
                    break;
                  }

                  return _context.abrupt("return");

                case 2:
                  middleware = [// https://floating-ui.com/docs/offset
                  middlewares.offset(function (_ref) {
                    var placement = _ref.placement; // If there's no placement alignment (*-start or *-end), we'll
                    // fallback to the crossAxis offset as it also works for
                    // center-aligned placements.

                    var hasAlignment = !!placement.split("-")[1];
                    return {
                      crossAxis: !hasAlignment ? shift : undefined,
                      mainAxis: finalGutter,
                      alignmentAxis: shift
                    };
                  })];

                  if (!(flip !== false)) {
                    _context.next = 8;
                    break;
                  }

                  fallbackPlacements = typeof flip === "string" ? flip.split(" ") : undefined;

                  if (!(fallbackPlacements !== undefined && !fallbackPlacements.every(isValidPlacement))) {
                    _context.next = 7;
                    break;
                  }

                  throw new Error("`flip` expects a spaced-delimited list of placements");

                case 7:
                  // https://floating-ui.com/docs/flip
                  middleware.push(middlewares.flip({
                    padding: overflowPadding,
                    fallbackPlacements: fallbackPlacements
                  }));

                case 8:
                  if (slide || overlap) {
                    // https://floating-ui.com/docs/shift
                    middleware.push(middlewares.shift({
                      mainAxis: slide,
                      crossAxis: overlap,
                      padding: overflowPadding
                    }));
                  } // https://floating-ui.com/docs/size


                  middleware.push(middlewares.size({
                    padding: overflowPadding,
                    apply: function apply(_ref2) {
                      var availableWidth = _ref2.availableWidth,
                          availableHeight = _ref2.availableHeight,
                          rects = _ref2.rects;
                      var referenceWidth = Math.round(rects.reference.width);
                      popover.style.setProperty("--popover-anchor-width", referenceWidth + "px");
                      popover.style.setProperty("--popover-available-width", availableWidth + "px");
                      popover.style.setProperty("--popover-available-height", availableHeight + "px");

                      if (sameWidth) {
                        popover.style.width = referenceWidth + "px";
                      }

                      if (fitViewport) {
                        popover.style.maxWidth = availableWidth + "px";
                        popover.style.maxHeight = availableHeight + "px";
                      }
                    }
                  }));

                  if (arrow) {
                    // https://floating-ui.com/docs/arrow
                    middleware.push(middlewares.arrow({
                      element: arrow,
                      padding: arrowPadding
                    }));
                  } // https://floating-ui.com/docs/computePosition


                  _context.next = 13;
                  return computePosition(anchor, popover, {
                    placement: placement,
                    strategy: fixed ? "fixed" : "absolute",
                    middleware: middleware
                  });

                case 13:
                  pos = _context.sent;
                  // Update the current placement state synchronously to avoid styling
                  // flashes. For example, without this, a popover that has initially
                  // placement set to "bottom", but gets flipped to "top" will have a
                  // frame where the popover arrow is not properly rotated (PopoverArrow
                  // uses the currentPlacement state).
                  flushSync(function () {
                    setCurrentPlacement(pos.placement);
                  });
                  x = Math.round(pos.x);
                  y = Math.round(pos.y); // https://floating-ui.com/docs/misc#subpixel-and-accelerated-positioning

                  Object.assign(popover.style, {
                    top: "0",
                    left: "0",
                    transform: "translate3d(" + x + "px, " + y + "px, 0)"
                  }); // https://floating-ui.com/docs/arrow#usage

                  if (arrow && pos.middlewareData.arrow) {
                    _pos$middlewareData$a = pos.middlewareData.arrow, arrowX = _pos$middlewareData$a.x, arrowY = _pos$middlewareData$a.y;
                    dir = pos.placement.split("-")[0];
                    Object.assign(arrow.style, _defineProperty({
                      left: arrowX != null ? arrowX + "px" : "",
                      top: arrowY != null ? arrowY + "px" : ""
                    }, dir, "100%"));
                  }

                case 19:
                case "end":
                  return _context.stop();
              }
            }
          }, _callee);
        }));

        return function update() {
          return _ref4.apply(this, arguments);
        };
      }(); // https://floating-ui.com/docs/autoUpdate


      return autoUpdate(anchor, popover, update, {
        // JSDOM doesn't support ResizeObserver
        elementResize: typeof ResizeObserver === "function"
      });
    };

    if (renderCallback) {
      return renderCallback({
        mounted: dialog.mounted,
        placement: placement,
        fixed: fixed,
        gutter: finalGutter,
        shift: shift,
        overlap: overlap,
        flip: flip,
        sameWidth: sameWidth,
        fitViewport: fitViewport,
        arrowPadding: arrowPadding,
        overflowPadding: overflowPadding,
        popover: popover,
        anchor: anchor,
        arrow: arrow,
        setPlacement: setCurrentPlacement,
        defaultRenderCallback: defaultRenderCallback
      });
    }

    return defaultRenderCallback();
  }, [rendered, dialog.contentElement, getAnchorRect, gutter, dialog.mounted, shift, overlap, flip, overflowPadding, slide, sameWidth, fitViewport, arrowPadding, placement, fixed, renderCallback]);
  var state = useMemo(function () {
    return _objectSpread(_objectSpread({}, dialog), {}, {
      getAnchorRect: getAnchorRect,
      anchorRef: anchorRef,
      popoverRef: popoverRef,
      arrowRef: arrowRef,
      currentPlacement: currentPlacement,
      placement: placement,
      fixed: fixed,
      gutter: gutter,
      shift: shift,
      flip: flip,
      slide: slide,
      overlap: overlap,
      sameWidth: sameWidth,
      fitViewport: fitViewport,
      arrowPadding: arrowPadding,
      overflowPadding: overflowPadding,
      render: render,
      renderCallback: renderCallback
    });
  }, [dialog, getAnchorRect, currentPlacement, placement, fixed, gutter, shift, flip, slide, overlap, sameWidth, fitViewport, arrowPadding, overflowPadding, render, renderCallback]);
  return state;
}

export { usePopoverState };